import {Node} from 'reactflow';

import isEqualWith from 'lodash/isEqualWith';
import last from 'lodash/last';
import sortBy from 'lodash/sortBy';

import {parseISODuration} from 'src/utils/ISODuration';

import {EChannelType} from '../../constants';
import {EMessageMatcherButtonTypes} from '../MessageMatchers/definitions';
import {TActionNodeData} from './Canvas/steps/Action/types';
import {TEdge, TNode, getDepthsMap} from './Canvas/types';
import {
  EFlowStepType,
  EUIActionType,
  EUIConditionType,
  EUIStepError,
  TActionStep,
  TConditionStep,
  TCycle,
  TCyclePair,
  TFlowStep,
  TTriggerStep,
  TUIActionPayload,
  TUIButtonPressedCondition,
  TUIConditionPayload,
  TUIPauseAction,
  TUITriggerPayload,
} from './types';

type TMapperMeta = {
  edges: TEdge[];
  nodes: TNode[];
};

const hasButtonPressedConditionErrors = (condition: TUIButtonPressedCondition) => {
  if (
    condition.buttonType === EMessageMatcherButtonTypes.CALLBACK ||
    condition.buttonType === EMessageMatcherButtonTypes.CHAT
  ) {
    return !condition.payload || !condition.text;
  }

  return !condition.text;
};

const ERROR_STEP_MAPPERS = {
  [EFlowStepType.ACTION]: (step: TActionStep<TUIActionPayload>, {edges, nodes}: TMapperMeta) => {
    const errors = [];

    if (step.payload.action === undefined) {
      errors.push(EUIStepError.MISSING_DATA);
    }

    if (
      step.payload.action?.type === EUIActionType.CREATE_MESSAGE_ACTION &&
      step.payload.action?.messageContent?.channelType === EChannelType.WHATSAPP
    ) {
      const incomingNodes = nodes.filter((node) =>
        edges.some((edge) => edge.source === node.id && edge.target === step.id),
      );

      const incomingActionNodes = incomingNodes.filter(
        (node) => node.data.step.type === EFlowStepType.ACTION,
      ) as Node<TActionNodeData>[];

      const incomingPauseActions = incomingActionNodes
        .filter((node) => node.data.step.payload.action?.type === EUIActionType.PAUSE_ACTION)
        .map((node) => node.data.step.payload.action) as TUIPauseAction[];

      if (
        incomingPauseActions.some((pauseAction) => parseISODuration(pauseAction.delay).days > 0)
      ) {
        errors.push(EUIStepError.MESSAGE_TIMEOUT_TOO_LONG);
      }
    }

    return errors;
  },
  [EFlowStepType.CONDITION]: (step: TConditionStep<TUIConditionPayload>, {edges}: TMapperMeta) => {
    const errors = [];

    if (!step.payload?.branches.length) {
      errors.push(EUIStepError.MISSING_DATA);
    }

    if (!edges.find((edge) => edge.source === step.id)) {
      errors.push(EUIStepError.NOT_LAST_FLOW_STEP);
    }

    if (
      step.payload?.branches?.some(
        (branch) =>
          branch.condition.type === EUIConditionType.BUTTON_PRESSED &&
          hasButtonPressedConditionErrors(branch.condition),
      )
    ) {
      errors.push(EUIStepError.EMPTY_BUTTON_CONDITION);
    }

    return errors;
  },
  [EFlowStepType.TRIGGER]: (step: TTriggerStep<TUITriggerPayload>) => {
    if (step.payload.condition.type === EUIConditionType.EMPTY) {
      return [EUIStepError.MISSING_TRIGGER_DATA];
    }

    return [];
  },
};

export const getFlowErrors = ({nodes, edges}: {nodes: TNode[]; edges: TEdge[]}) => {
  const depthsMap = getDepthsMap({nodes, edges});

  return (
    nodes
      // node is used in graph
      .filter((step) => depthsMap[step.id] !== undefined)
      .reduce((errors, node) => {
        // @ts-expect-error: TODO: research how to bind EFlowStepType to exact TFlowStep
        const nodeErrors = ERROR_STEP_MAPPERS[node.data.step.type](node.data.step, {edges, nodes});

        if (!nodeErrors.length) {
          return errors;
        }

        return {
          ...errors,
          [node.data.step.id]: nodeErrors,
        };
      }, {})
  );
};

export const getCyclePairs = (cycles: TCycle) =>
  cycles.reduce((acc: TCyclePair[], stepId, index) => {
    const lastPair = last(acc);

    if (lastPair) {
      lastPair.target = stepId;
    }

    if (index === cycles.length - 1) {
      acc.push({source: stepId, target: cycles[0]});
    } else {
      acc.push({source: stepId} as TCyclePair);
    }

    return acc;
  }, []) as TCyclePair[];

const IGNORED_KEYS = [
  'depth',
  'meta',
  'flowId',
  'createdAt',
  'updatedAt',
  'enabled',
  'updatedBy',
  'visitCount',
];

export const checkStepsChanged = (initialSteps: TFlowStep[], steps: TFlowStep[]): boolean =>
  !isEqualWith(
    sortBy(initialSteps, 'id'),
    sortBy(steps, 'id'),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (_, __, key: any, initialParent: any, parent: any) => {
      if (
        IGNORED_KEYS.includes(key) ||
        (initialParent?.type === 'EDGE' && parent?.type === 'EDGE' && key === 'id')
      ) {
        return true;
      }
    },
  );
